<template>
    <div>
        <v-row no-gutters align="center" justify="end">
            <v-btn depressed color="primary" rounded @click="openCreateDialog">
                <v-icon left>mdi-plus</v-icon>
                <span>New Document</span>
            </v-btn>

            <DocumentForm
                v-model="createDialog"
                title="New Document"
                :create-form="true"
                @closeDialog="closeCreateDialog"
                @addDocument="addDocument"
                formUser=""
                :clientId="clientId"
                :document-types="documentTypes"
            />
        </v-row>
        <v-data-table
            v-if="client"
            :items-per-page="6"
            :loading="loading"
            :items="documents"
            :headers="headers"
            class="my-4"
            :mobile-breakpoint="0"
        >
            <template v-slot:[`item.filename`]="{ item }">
                <a @click="click(item)">{{ item.filename }}</a></template
            >
            <template v-slot:[`item.statusLabel`]="{ item }">
                <div class="d-flex align-center">
                    <v-chip
                        v-if="item.statusLabel"
                        small
                        :color="item.statusColor"
                        dark
                    >
                        {{ item.statusLabel }}
                    </v-chip>
                    <v-btn
                        v-if="item.status === 'REJECTED'"
                        icon
                        x-small
                        class="ml-1"
                        @click.stop="
                            openRejectionInfo(
                                item.rejectionNotes ||
                                    item.rejectNotes ||
                                    item.notes
                            )
                        "
                    >
                        <v-icon small color="info">
                            mdi-information-outline
                        </v-icon>
                    </v-btn>
                </div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon small @click="deleteItem(item)">
                    <v-icon small>mdi-trash-can</v-icon>
                </v-btn>

                <v-dialog
                    v-model="deleteDialog"
                    :retain-focus="false"
                    persistent
                    max-width="400px"
                >
                    <v-card>
                        <v-card-title class="text-h5"
                            >Delete Document</v-card-title
                        >
                        <v-card-text>
                            Are you sure you want to delete this document?
                        </v-card-text>
                        <v-card-actions>
                            <v-btn
                                text
                                color="secondary"
                                @click="deleteDialog = false"
                            >
                                Close
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn @click="deleteDoc" text color="error">
                                Delete
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </template>
        </v-data-table>
        <v-dialog v-model="rejectionInfoDialog" max-width="420px">
            <v-card>
                <v-card-title class="text-h6">Rejection reasons</v-card-title>
                <v-card-text>
                    <v-textarea
                        :value="rejectionInfoText"
                        readonly
                        outlined
                        hide-details
                        rows="4"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        text
                        color="primary"
                        @click="rejectionInfoDialog = false"
                    >
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { storage } from '@/services/firebase'
import DocumentForm from '@/components/Settings/DocumentForm'
import API from '@/services/api'
import moment from 'moment'
import { mapMutations } from 'vuex'
export default {
    name: 'ClientDocuments',
    props: {
        id: String,
        client: Object,
    },
    components: {
        DocumentForm,
    },
    data() {
        return {
            folderDocuments: 'clients_documents',
            companyId: JSON.parse(localStorage.getItem('company')),
            createDialog: false,
            loading: false,
            documents: [],
            error: null,
            deleteDialog: false,
            rejectionInfoDialog: false,
            rejectionInfoText: '',
            docToDelete: null,
            errorMsg: null,
            indexItem: null,
            clientId: null,
            documentTypes: [],
            headers: [
                { text: 'DATE', value: 'createdOn', class: 'grey lighten-3' },
                { text: 'DESCRIPTION', value: 'name', class: 'grey lighten-3' },
                {
                    text: 'DOCUMENT',
                    value: 'filename',
                    class: 'grey lighten-3',
                },
                { text: 'TYPE', value: 'typeLabel', class: 'grey lighten-3' },
                {
                    text: 'EXPIRATION',
                    value: 'expirationDateFormatted',
                    class: 'grey lighten-3',
                },
                {
                    text: 'STATUS',
                    value: 'statusLabel',
                    sortable: false,
                    class: 'grey lighten-3',
                },
                {
                    text: 'ACTIONS',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    class: 'grey lighten-3',
                },
            ],
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        getStatusMeta(status) {
            if (status === 'APPROVED') {
                return {
                    statusLabel: 'Approved',
                    statusColor: 'success',
                }
            }
            if (status === 'REJECTED') {
                return {
                    statusLabel: 'Rejected',
                    statusColor: 'error',
                }
            }
            if (status === 'PENDING') {
                return {
                    statusLabel: 'Pending',
                    statusColor: 'orange',
                }
            }
            return {
                statusLabel: '',
                statusColor: '',
            }
        },
        isSpecialType(type) {
            return this.documentTypes.map(d => d.key).includes(type)
        },
        getTypeLabel(type) {
            const docType = this.documentTypes.find(d => d.key === type) || null
            return docType ? docType.label : ''
        },
        formatDocDate(val, pattern = 'MMM/DD/YYYY HH:mm') {
            if (!val) return '—'
            const m =
                val._seconds != null ? moment.unix(val._seconds) : moment(val)
            return m.isValid() ? m.format(pattern) : '—'
        },
        openCreateDialog: function() {
            this.createDialog = true
        },
        async deleteClientDoc() {
            try {
                this.loading = true
                this.error = false
                this.errorMsg = null
                const file = this.docToDelete.file
                const filename = this.docToDelete.filename
                const fileExt =
                    file && file.includes('.') ? file.split('.').pop() : null

                const nameExt =
                    filename && filename.includes('.')
                        ? filename.split('.').pop()
                        : null

                if (!fileExt && nameExt) {
                    this.docToDelete.file = `${file}.${nameExt}`
                }
                if (this.docToDelete.file) {
                    await this.deleteOnStorage(this.docToDelete.file)
                }
                await API.deleteDocClient(this.docToDelete.id)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.deleteDialog = false
            }
        },
        async deleteOnStorage(file) {
            const path = `${this.companyId}/${this.folderDocuments}/${this.id}`
            const storageRef = storage().ref(path)
            const refDoc = storageRef.child(file)
            await refDoc.delete().catch(error => {
                console.log(error.message)
            })
        },
        closeCreateDialog() {
            this.createDialog = false
        },
        deleteItem(item) {
            this.deleteDialog = true
            this.docToDelete = item
            this.indexItem = this.documents.findIndex(
                x => x.id == this.docToDelete.id
            )
        },
        addDocument(document) {
            if (document.specialClientDoc) {
                if (!document.document) {
                    return
                }
                const specialDocument = {
                    ...document.document,
                    createdOn: this.formatDocDate(document.document.createdOn),
                    typeLabel: this.getTypeLabel(document.document.type),
                    expirationDateFormatted: this.formatDocDate(
                        document.document.expirationDate,
                        'MMM/DD/YYYY'
                    ),
                    ...this.getStatusMeta(document.document.status),
                    filename: document.document.name,
                }
                const currentIndex = this.documents.findIndex(
                    item => item.id === specialDocument.id
                )
                if (currentIndex >= 0) {
                    this.$set(this.documents, currentIndex, specialDocument)
                } else {
                    this.documents.push(specialDocument)
                }
                return
            }
            this.documents.push({
                ...document,
                ...this.getStatusMeta(document.status),
            })
        },
        click(event) {
            window.location.assign(event.url)
        },
        openRejectionInfo(notes) {
            this.rejectionInfoText = (notes || '').toString().trim() || '—'
            this.rejectionInfoDialog = true
        },
        deleteDoc() {
            this.documents.splice(this.indexItem, 1)
            this.deleteClientDoc()
        },
    },

    async created() {
        try {
            this.loading = true
            this.clientId = this.id
            const {
                data: { settings },
            } = await API.getSettings()
            const clientSetting = settings.find(s => s.name === 'Client')
            if (clientSetting?.id) {
                const formats = await API.getClientSalesFormats(
                    clientSetting.id
                )
                this.documentTypes = formats.documentTypes || []
            }
            const {
                data: { documents },
            } = await API.getClientDocuments(this.id, this.companyId)
            this.documents = documents.filter(
                doc => !(this.isSpecialType(doc.type) && !doc.file)
            )
            this.documents.forEach(document => {
                document.createdOn = this.formatDocDate(document.createdOn)
                document.typeLabel = this.getTypeLabel(document.type)
                document.expirationDateFormatted = this.formatDocDate(
                    document.expirationDate,
                    'MMM/DD/YYYY'
                )
                Object.assign(document, this.getStatusMeta(document.status))
            })
            for (const doc of this.documents) {
                if (!doc.file) {
                    continue
                }
                if (!doc.url) {
                    const path = `${this.companyId}/${this.folderDocuments}/${this.id}`
                    const storageRef = storage().ref(path)
                    const result = await storageRef.listAll()
                    let match
                    if (doc.file.includes('.')) {
                        match = result.items.find(
                            item => item.name === doc.file
                        )
                    } else {
                        match = result.items.find(item =>
                            item.name.startsWith(doc.file)
                        )
                    }
                    if (match) {
                        doc.file = match.name
                        doc.url = await match.getDownloadURL().catch(error => {
                            console.log(error.message)
                        })
                        if (doc.url) {
                            const meta = await match.getMetadata()
                            doc.filename = meta.customMetadata.filename.replaceAll(
                                '+',
                                ' '
                            )
                        }
                    } else {
                        console.warn(`No file found for: ${doc.file}`)
                    }
                }
            }
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
}
</script>

<style scoped>
.text-singleline {
    line-height: 0.75rem;
}
.v-data-table >>> table {
    border: thin solid #eeeeee;
}
.no-border {
    border: none !important;
}
tr:hover {
    background: unset !important;
}
tr:not(:last-child) > td.border-grey {
    border-bottom: thin solid #eeeeee !important;
}
</style>
